import { FormEvent, Fragment } from 'react';
import { ICenterBase } from '@nkr/models/index';
import { IAvailableRecipient } from '@nkr/store/index';
import { SwapManagementAction } from './swap-managerment-action';
import { Alert, Button, FormControl, Glyphicon, Label, Table } from 'react-bootstrap';
import SwapManagementRow from './row';
import { ISwapManagementPosition } from './swap-management-position';
import styled from 'styled-components';

const TableWrapper = styled(Table)`
    & > thead > tr > th,
    & > tbody > tr > td {
        text-align: center;
    }

    & > thead > tr > th {
        border: 1px solid white;
    }

    & > tbody > tr > td {
        vertical-align: middle;
    }

    & > tbody > tr.highlight,
    & > tbody > tr > td.highlight {
        background-color: #DEF9FF;
    }
`;

interface IProps {
    sortedPositions: ISwapManagementPosition[],
    lastPositionAcrossAllClusters: any,
    action: SwapManagementAction | number,
    clusterNumber: number,
    index: number,
    firstPositionOfCluster: number,
    maxClusterNumber: number,
    center: number,
    recipient: number,
    donor: number,
    donorToBridgeAlias: string,
    lastRecipientCenter: string,
    lastRecipientAlias: string,
    lastPairCenter: string,
    recipientList: IAvailableRecipient[],
    centerList: ICenterBase[],
    onAction: (action: SwapManagementAction, index: number) => void,
    onResetCluster: (clusterId: number, donorId: number) => void,
    onCenterChange: (id: number) => void,
    onRecipientChange: (id: number) => void,
    onDonorChange: (id: number) => void,
    onYes: () => void,
    onNo: () => void
}

const SwapManagementContent = (props: IProps) => {
    const handleCenterChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        props.onCenterChange(parseInt(e.currentTarget.value));
    };

    const handleRecipientChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        props.onRecipientChange(parseInt(e.currentTarget.value));
    };

    const handleDonorChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        props.onDonorChange(parseInt(e.currentTarget.value));
    };

    const renderOptions = () => {
        let recipientList = props.recipientList.filter(x => x.centerId == props.center);
        let currentRecipient = recipientList.find(x => x.id == props.recipient);
        let bridgeDonorList = currentRecipient != null ? currentRecipient.pairedDonors : [];

        let currentPosition = props.sortedPositions.find(x => x.index == props.index);
        let swapNumber = currentPosition != null ? `${currentPosition.chainId}/${currentPosition.clusterNumber}` : '';

        let yesButton = [SwapManagementAction.ReplacePair, SwapManagementAction.ReplaceRecipient].includes(props.action) ? 'Submit' : 'Yes';
        let noButton = [SwapManagementAction.ReplacePair, SwapManagementAction.ReplaceRecipient].includes(props.action) ? 'Cancel' : 'No';

        return (
            <tr className='highlight'>
                <td colSpan={8} style={{ whiteSpace: 'normal' }}>
                    <div style={{ padding: 5 }}>
                        {props.action == SwapManagementAction.Unbridge && (
                            <div style={{ marginBottom: 10, color: '#133366', fontWeight: 550 }}>
                                Are you sure you want to re-link/unbridge donor {props.donorToBridgeAlias}?
                            </div>
                        )}

                        {props.action == SwapManagementAction.Bridge && (
                            <div style={{ marginBottom: 10, color: '#133366', fontWeight: 550 }}>
                                Are you sure you want to bridge donor {props.donorToBridgeAlias}?
                            </div>
                        )}

                        {props.action == SwapManagementAction.ResetCluster && (
                            <>
                                <div style={{ marginBottom: 5 }}>
                                    This will clear cluster #{props.clusterNumber} of every position. This will also wipe out the donor logistics, and the
                                    recipient logistics.
                                </div>

                                <div style={{ marginBottom: 5 }}>
                                    <span style={{ color: 'red', fontWeight: 600 }}>WARNING! This is non-recoverable.</span>
                                </div>

                                <div style={{ marginBottom: 10, color: '#133366', fontWeight: 550 }}>
                                    Are you sure you wish to continue?
                                </div>
                            </>
                        )}

                        {props.action == SwapManagementAction.NewChainAsVoucher && (
                            <>
                                <div style={{ marginBottom: 5 }}>
                                    This will convert the donor to a Voucher donor and put the donor in the next available chain number.
                                </div>

                                <div style={{ marginBottom: 5 }}>
                                    After conversion to a Voucher, the donor may no longer be exportable until you upload the Voucher Consent Form or<br />
                                    convert them to a swap saver.
                                </div>

                                <div style={{ marginBottom: 10, color: '#133366', fontWeight: 550 }}>
                                    Are you sure you want to create a new chain as a voucher with donor {props.donorToBridgeAlias}?
                                </div>
                            </>
                        )}

                        {props.action == SwapManagementAction.RemoveRecipient && (
                            <>
                                <div style={{ marginBottom: 10, color: '#133366', fontWeight: 550 }}>
                                    Are you sure you want to remove recipient {props.lastRecipientAlias} from this position?
                                </div>
                            </>
                        )}

                        {props.action == SwapManagementAction.RemovePair && (
                            <>
                                <div style={{ marginBottom: 10, color: '#133366', fontWeight: 550 }}>
                                    Are you sure you want to remove the {props.lastPairCenter} pair?
                                </div>
                            </>
                        )}

                        {(props.action == SwapManagementAction.ReplacePair || props.action == SwapManagementAction.ReplaceRecipient || props.action == SwapManagementAction.AddRecipient) && (
                            <>
                                <div style={{ marginBottom: 10, color: '#133366', fontWeight: 550 }}>
                                    <div style={{ marginBottom: 10 }}>
                                        {props.action == SwapManagementAction.ReplacePair && (
                                            <>Please specify the replacement for the {props.lastPairCenter} pair...</>
                                        )}

                                        {props.action == SwapManagementAction.ReplaceRecipient && (
                                            <>Please specify the replacement recipient for {props.lastRecipientAlias} @ {props.lastRecipientCenter}...</>
                                        )}

                                        {props.action == SwapManagementAction.AddRecipient && (
                                            <>Please specify the recipient to add to swap {swapNumber}...</>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                                            <div style={{ paddingRight: 5 }}>Center:</div>
                                            <div>
                                                <FormControl
                                                    componentClass='select'
                                                    style={{ fontWeight: 'normal', width: 180 }}
                                                    value={props.center}
                                                    onChange={handleCenterChange}
                                                >
                                                    {props.centerList.map(center =>
                                                        <option key={'ctr-' + center.id} value={center.id}>{center.alias}</option>
                                                    )}
                                                </FormControl>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                                            <div style={{ paddingRight: 5 }}>Recipient:</div>
                                            <div>
                                                <FormControl
                                                    componentClass='select'
                                                    style={{ fontWeight: 'normal', width: 180 }}
                                                    value={props.recipient}
                                                    onChange={handleRecipientChange}
                                                >
                                                    {recipientList.map(recipient =>
                                                        <option key={'recp-' + recipient.id} value={recipient.id}>{recipient.alias}</option>
                                                    )}
                                                </FormControl>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <div style={{ paddingRight: 5 }}>Bridge Donor:</div>
                                            <div>
                                                {bridgeDonorList.length > 1 ?
                                                    <FormControl
                                                        componentClass='select'
                                                        style={{ fontWeight: 'normal', width: 180 }}
                                                        value={props.donor}
                                                        onChange={handleDonorChange}
                                                    >
                                                        {bridgeDonorList.map(donor =>
                                                            <option key={'bd-' + donor.id} value={donor.id}>{donor.alias}</option>
                                                        )}
                                                    </FormControl>
                                                    :
                                                    <Label bsStyle={bridgeDonorList.length == 1 ? 'primary' : 'default'} style={{ fontSize: '100%', width: 180, display: 'inline-block', padding: '8px 12px' }}>{bridgeDonorList.length == 1 ? bridgeDonorList[0].alias : 'no bridge donor'}</Label>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        <div style={{ textAlign: 'center' }}>
                            <Button bsStyle='primary' onClick={props.onYes}>{yesButton}</Button>&nbsp;
                            <Button onClick={props.onNo}>{noButton}</Button>
                        </div>
                    </div>
                </td>
            </tr>
        )
    };

    return (
        <>
            <Alert bsStyle='warning' style={{ textAlign: 'center', paddingTop: 7.5, paddingBottom: 7.5 }}>
                <Glyphicon glyph='warning-sign' /> USE WITH CAUTION! Please verify after doing ANY action that swaps still have correct status/manager/courier, etc.
            </Alert>

            <TableWrapper condensed style={{ marginBottom: 0 }}>
                <thead style={{ backgroundColor: '#ddd' }}>
                    <tr>
                        <th rowSpan={2} style={{ width: '10%' }}>Swap</th>
                        <th colSpan={2}>Donor</th>
                        <th rowSpan={2}></th>
                        <th colSpan={2}>Recipient</th>
                        <th rowSpan={2} style={{ width: '20%' }}>Actions</th>
                    </tr>
                    <tr>
                        <th style={{ width: '15%' }}>Center</th>
                        <th style={{ width: '15%' }}>Alias</th>
                        <th style={{ width: '15%' }}>Center</th>
                        <th style={{ width: '15%' }}>Alias</th>
                    </tr>
                </thead>
                <tbody>
                    {props.sortedPositions.map((position, index) => {
                        return (
                            <Fragment key={'sw-' + index}>
                                {props.action != SwapManagementAction.ResetCluster && position.isNotCurrent && position.clusterNumber == props.maxClusterNumber && (
                                    <tr>
                                        <td colSpan={8} style={{ backgroundColor: '#133366' }}>
                                            <span style={{ fontSize: 14, color: 'yellow', cursor: 'default' }} onClick={() => props.onResetCluster(position.clusterId, position.donor.id)}>
                                                <Glyphicon glyph='remove-circle' title='Clear Cluster' />&nbsp;&nbsp;
                                                <span>Click here to reset all positions of the cluster below.</span>
                                            </span>
                                        </td>
                                    </tr>
                                )}

                                {props.action == SwapManagementAction.ResetCluster
                                    && index == props.firstPositionOfCluster
                                    && renderOptions()}

                                <SwapManagementRow
                                    data={position}
                                    isLastPosition={props.lastPositionAcrossAllClusters.clusterNumber == position.clusterNumber && props.lastPositionAcrossAllClusters.positionNumber == position.positionNumber}
                                    action={props.action}
                                    currentIndex={props.index}
                                    onAction={props.onAction}
                                />

                                {props.action != SwapManagementAction.Select
                                    && props.action != SwapManagementAction.ResetCluster
                                    //&& (position.clusterId == clusterForAction && position.positionNumber == positionForAction)
                                    && (position.index == props.index)
                                    && renderOptions()}
                            </Fragment>
                        );
                    })}
                </tbody>
            </TableWrapper>
        </>
    );
}

export default SwapManagementContent;